import { useEffect, useState } from 'react';
import Modal from './Modal';
import PasswordField from './PasswordField';
import { api } from '../../lib/api';
import { toHumanMessage } from '../../lib/errors';
import { useSession } from '../../lib/session';
import { onSessionExpired } from '../../lib/sessionEvents';

// Se muestra sobre la pantalla actual (sin navegar a ningún lado) en cuanto
// cualquier request autenticado recibe un 401 — cubre el caso de sesión expirada
// a media sesión (cookie vencida/borrada) sin tirar al usuario a errores crudos
// por pantalla. Al reautenticar, cierra y la pantalla en la que estaba sigue
// intacta (coordina con el guard de cambios sin guardar de UX-21: como no hay
// navegación, un borrador abierto en el editor no se pierde).
export default function SessionExpiredModal() {
  const { user, refresh } = useSession();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    return onSessionExpired(() => {
      setOpen(true);
      setPassword('');
      setError('');
    });
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user?.email || !password) return;
    setLoading(true);
    setError('');
    try {
      await api.auth.login(user.email, password);
      await refresh();
      setOpen(false);
      setPassword('');
    } catch (err) {
      setError(toHumanMessage(err, 'No pudimos volver a entrar. Revisa tu contraseña e inténtalo de nuevo.'));
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;

  return (
    <Modal title="Tu sesión terminó" onClose={() => { /* no se puede cerrar sin re-entrar */ }}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-ui-text-muted">
          Tu sesión terminó por seguridad. Vuelve a entrar para seguir donde te quedaste — nada de lo que
          tenías abierto se pierde.
        </p>
        <div>
          <label className="block text-sm text-ui-text-muted mb-1">Correo</label>
          <input
            type="email"
            value={user?.email ?? ''}
            disabled
            className="w-full bg-ui-bg border border-ui-border rounded-lg px-3 py-2 text-sm text-ui-text-subtle"
          />
        </div>
        <PasswordField
          id="session-expired-password"
          label="Contraseña"
          value={password}
          onChange={setPassword}
          variant="current"
          autoFocus
        />
        {error && <p className="text-sm text-ui-danger">{error}</p>}
        <button
          type="submit"
          disabled={loading || !password}
          className="w-full px-4 py-2.5 bg-ui-accent text-ui-text-inverse text-sm font-medium rounded-lg disabled:opacity-50 hover:opacity-90 transition-opacity"
        >
          {loading ? 'Entrando…' : 'Entrar y continuar'}
        </button>
      </form>
    </Modal>
  );
}
